import React, { useState } from 'react';
import { PaperData } from './types/paper';
import { seedAttentionIsAllYouNeed } from './data/seedAttentionIsAllYouNeed';
import { seedQuantumKeyDistribution } from './data/seedQuantumKeyDistribution';
import { seedCrisprGeneEditing } from './data/seedCrisprGeneEditing';
import { Navbar } from './components/Navbar';
import { ConceptWiki } from './components/ConceptWiki';
import { ConceptGraph } from './components/ConceptGraph';
import { FlashcardStudio } from './components/FlashcardStudio';
import { MonsterBattle } from './components/MonsterBattle';
import { PaperIngestModal } from './components/PaperIngestModal';
import { ApiKeyModal } from './components/ApiKeyModal';
import { ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

export function App() {
  const [paper, setPaper] = useState<PaperData>(seedCrisprGeneEditing);
  const [activeTab, setActiveTab] = useState<'wiki' | 'lineage' | 'flashcards' | 'battle'>('flashcards');
  const [isIngestOpen, setIsIngestOpen] = useState<boolean>(false);
  const [isKeyOpen, setIsKeyOpen] = useState<boolean>(false);
  const [showAbstract, setShowAbstract] = useState<boolean>(false);

  const handleSelectInstantDemo = (demoId: 'transformer' | 'quantum' | 'crispr') => {
    if (demoId === 'transformer') {
      setPaper(seedAttentionIsAllYouNeed);
    } else if (demoId === 'quantum') {
      setPaper(seedQuantumKeyDistribution);
    } else {
      setPaper(seedCrisprGeneEditing);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg text-slate-100 flex flex-col font-sans">
      {/* Top Navbar */}
      <Navbar
        currentPaper={paper}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenIngestModal={() => setIsIngestOpen(false)}
        onOpenKeyModal={() => setIsKeyOpen(true)}
        onSelectInstantDemo={handleSelectInstantDemo}
      />

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-8 space-y-6">
        
        {/* Paper / Topic Banner Header */}
        <section className="glass-panel rounded-2xl p-6 border border-slate-800 shadow-xl relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-80 h-80 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="px-3 py-1 rounded-full bg-brand-900/60 text-brand-300 border border-brand-500/30 font-semibold font-mono">
                  {paper.arxivId ? `arXiv:${paper.arxivId}` : 'Topic / Literature Domain'}
                </span>
                <span className="text-slate-400">• {paper.year}</span>
                <span className="text-slate-400">• {paper.concepts.length} Core Concepts</span>
                <span className="text-slate-400">• {paper.flashcards.length} Flashcards</span>
              </div>

              <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                {paper.title}
              </h1>

              <p className="text-xs sm:text-sm text-slate-300 font-medium">
                {paper.authors.join(', ')}
              </p>
            </div>

            {/* ArXiv Link & Overview Toggle */}
            <div className="flex items-center gap-3 shrink-0">
              {paper.pdfUrl && (
                <a
                  href={paper.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-4 py-2 bg-dark-card hover:bg-slate-800 text-cyan-300 border border-slate-700 rounded-xl text-xs font-semibold transition-all shadow-sm"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Read PDF
                </a>
              )}

              <button
                onClick={() => setShowAbstract(!showAbstract)}
                className="flex items-center gap-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-semibold transition-all"
              >
                Overview {showAbstract ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Abstract / Topic Overview Drawer */}
          {showAbstract && (
            <div className="mt-4 pt-4 border-t border-slate-800 text-xs sm:text-sm text-slate-300 leading-relaxed animate-fade-in bg-dark-bg/60 p-4 rounded-xl">
              <strong className="text-white">Summary / Overview:</strong> {paper.abstract}
            </div>
          )}
        </section>

        {/* Dynamic Active View */}
        <section className="transition-all duration-300">
          {activeTab === 'flashcards' && (
            <FlashcardStudio paper={paper} />
          )}

          {activeTab === 'wiki' && (
            <ConceptWiki concepts={paper.concepts} paperTitle={paper.title} />
          )}

          {activeTab === 'lineage' && (
            <ConceptGraph graph={paper.lineageGraph} paperTitle={paper.title} />
          )}

          {activeTab === 'battle' && (
            <MonsterBattle paper={paper} />
          )}
        </section>

      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-slate-800/80 py-6 text-center text-xs text-slate-500">
        ScholarWiki & Flashcard Studio • Powered by Gemini AI Studio & Cloudflare Pages
      </footer>

      {/* Modals */}
      <PaperIngestModal
        isOpen={isIngestOpen}
        onClose={() => setIsIngestOpen(false)}
        onPaperProcessed={(newPaper) => {
          setPaper(newPaper);
          setActiveTab('flashcards');
        }}
      />

      <ApiKeyModal
        isOpen={isKeyOpen}
        onClose={() => setIsKeyOpen(false)}
      />
    </div>
  );
}

export default App;
